import { T } from '@tolgee/react';
import { Tooltip } from 'antd';
import Link from 'next/link';
import { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import ChevronRight from '../../../assets/icons/ChevronRight';
import theme from '../../../assets/theme';

interface TeachingCreditCounterProps {
  remaining: number;
  bonusRemaining: number;
  bonusLimit: number;
  weeklyRate: number;
  used: number;
  renewsAt?: string;
  onGetMore?: () => void;
  unlimited?: boolean;
}

// Teaching sessions use the purple palette so they're visually distinct from
// the orange School plan trial counter that sits below them in the sidebar.
// Weekly is the primary purple; bonus is a lighter shade so teachers can
// still read the stacked bar as "weekly vs. bonus" at a glance.
const BONUS_COLOR = '#D1B3FF';
const WEEKLY_COLOR = theme.colors.purple;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.greyDark};
  line-height: 1;
`;

// Chevron fades in when the parent Container is hovered, signalling the row
// is clickable. Kept subtle so it doesn't compete with the progress bar.
const HoverChevron = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${Container}:hover & {
    opacity: 0.6;
  }
`;

const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #e8e0d8;
  border-radius: 3px;
  overflow: hidden;
  display: flex;
`;

const BarSegment = styled.div<{
  $width: number;
  $color: string;
  $dimmed?: boolean;
}>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  transition: width 0.3s ease, opacity 0.15s ease;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.3 : 1)};
`;

const EmptySegment = styled.div<{ $width: number }>`
  height: 100%;
  flex: 1;
  min-width: ${({ $width }) => $width}%;
`;

const Sublabel = styled.span`
  font-size: 11px;
  color: ${theme.colors.greyDark};
  opacity: 0.6;
  line-height: 1;
`;

const formatRenewalDay = (renewsAt: string) => {
  try {
    const date = new Date(renewsAt);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Sunday';
  }
};

export const TeachingCreditCounter: FunctionComponent<TeachingCreditCounterProps> = ({
  remaining,
  bonusRemaining,
  bonusLimit,
  weeklyRate,
  used,
  renewsAt,
  onGetMore,
  unlimited,
}) => {
  const [hovered, setHovered] = useState<'bonus' | 'weekly' | 'empty' | null>(null);

  const renewalDay = renewsAt ? formatRenewalDay(renewsAt) : 'Monday';
  const hasSessions = remaining > 0;

  const weeklyUsed = Math.min(used, weeklyRate);
  const weeklyRemaining = weeklyRate - weeklyUsed;

  // bonusLimit is the user's original bonus allotment and stays constant, giving
  // the bar a stable denominator. bonusRemaining can exceed bonusLimit if an admin
  // grants extra before the initial allotment is consumed — clamp the fill at the
  // denominator so we never render > 100%.
  const totalForBar = bonusLimit + weeklyRate;
  const bonusFill = Math.min(bonusRemaining, bonusLimit);
  const bonusPercent = totalForBar > 0 ? (bonusFill / totalForBar) * 100 : 0;
  const weeklyPercent = totalForBar > 0 ? (weeklyRemaining / totalForBar) * 100 : 0;
  const emptyPercent = 100 - bonusPercent - weeklyPercent;

  const getTooltipText = () => {
    if (hovered === 'bonus') {
      return `${bonusRemaining} bonus sessions remaining`;
    }
    if (hovered === 'weekly') {
      return `${weeklyRemaining} of ${weeklyRate} weekly sessions remaining`;
    }
    if (hovered === 'empty') {
      if (bonusRemaining === 0) return '0 bonus sessions remaining';
      return `${weeklyRate - weeklyRemaining} of ${weeklyRate} weekly sessions used`;
    }
    return '';
  };

  const tooltipText = getTooltipText();

  const handleClick = onGetMore
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onGetMore();
      }
    : undefined;

  if (unlimited) {
    return (
      <Link href="/profile" passHref>
        <a style={{ textDecoration: 'none' }}>
          <Container>
            <LabelRow>
              <Label>
                <T
                  keyName="LessonCreditCounter.Unlimited"
                  defaultValue="∞ Unlimited teaching sessions"
                />
              </Label>
              <HoverChevron>
                <ChevronRight color={theme.colors.greyDark} />
              </HoverChevron>
            </LabelRow>
            <BarTrack>
              <BarSegment $width={100} $color={WEEKLY_COLOR} />
            </BarTrack>
            <Sublabel>
              <T
                keyName="LessonCreditCounter.IncludedInPlan"
                defaultValue="Included in your plan"
              />
            </Sublabel>
          </Container>
        </a>
      </Link>
    );
  }

  return (
    <Link href="/profile" passHref>
      <a style={{ textDecoration: 'none' }} onClick={handleClick}>
        <Container>
          <LabelRow>
            <Label>
              {hasSessions ? (
                <T
                  keyName="TeachingCreditCounter.SessionsLeftThisWeek"
                  defaultValue="{remaining} teaching {remaining, plural, one {session} other {sessions}} left this week"
                  params={{ remaining }}
                />
              ) : (
                <T
                  keyName="TeachingCreditCounter.NoSessionsLeftThisWeek"
                  defaultValue="0 teaching sessions left this week"
                />
              )}
            </Label>
            <HoverChevron>
              <ChevronRight color={theme.colors.greyDark} />
            </HoverChevron>
          </LabelRow>

          <Tooltip
            title={tooltipText}
            visible={!!tooltipText && hovered !== null}
            placement="right"
          >
            <BarTrack onMouseLeave={() => setHovered(null)}>
              {bonusPercent > 0 && (
                <BarSegment
                  $width={bonusPercent}
                  $color={BONUS_COLOR}
                  $dimmed={hovered !== null && hovered !== 'bonus'}
                  onMouseEnter={() => setHovered('bonus')}
                />
              )}
              {weeklyPercent > 0 && (
                <BarSegment
                  $width={weeklyPercent}
                  $color={WEEKLY_COLOR}
                  $dimmed={hovered !== null && hovered !== 'weekly'}
                  onMouseEnter={() => setHovered('weekly')}
                />
              )}
              {emptyPercent > 0 && (
                <EmptySegment
                  $width={emptyPercent}
                  onMouseEnter={() => setHovered('empty')}
                />
              )}
            </BarTrack>
          </Tooltip>

          <Sublabel>
            {hasSessions ? (
              <T
                keyName="LessonCreditCounter.Renews"
                defaultValue="{weeklyRate} per week · resets {renewalDay}"
                params={{ weeklyRate, renewalDay }}
              />
            ) : (
              <T keyName="LessonCreditCounter.GetMore" defaultValue="Get more" />
            )}
          </Sublabel>
        </Container>
      </a>
    </Link>
  );
};
